// libs
import React, { useCallback, useState } from 'react';

import { Trans, useTranslation } from '@lyearn/i18n';
import { Certificate2Icon } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
// components
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import { CertificateTemplateStatusEnum } from '@/types/schema';

import LocalisedSttingInfo from '../../../course/views/CourseBuilder/components/SidebarDetails/Settings/components/Dialogs/components/Certificate/localizedSettingInfo';
// hooks
import useCertificateTemplates from '../../hooks/useCertificateTemplates';
import CertificateCard from '../CertificateCard';

function AttachCertificateDialog(props: {
  title?: string;
  description?: string;
  actionText?: string;
  onAttach: (certificateId: string) => void;
  onClose: () => void;
  selectedCertificateId?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const {
    title = t('Certificate:Form.AttachACertificate'),
    description = t('Certificate:Form.CertificateDescription'),
    actionText = t('Certificate:Form.AttachCertificate'),
    onAttach,
    onClose,
    selectedCertificateId,
    disabled,
  } = props;

  const { certificateTemplates, initialFetching, fetchMore } = useCertificateTemplates({
    filters: { status: [CertificateTemplateStatusEnum.Active] },
  });
  const [selectedItem, setSelectedItem] = useState<string>(selectedCertificateId || '');
  const attachCertificate = useCallback(() => {
    onAttach(selectedItem);
  }, [onAttach, selectedItem]);

  const onSelect = useCallback((certificateId: string) => {
    setSelectedItem(certificateId);
  }, []);

  return (
    <ContentListModal.Main
      action={
        certificateTemplates?.length && !disabled
          ? {
              discardText: t('Common:Cancel'),
              onSubmit: attachCertificate,
              text: actionText,
              disabled: !selectedItem || disabled,
            }
          : undefined
      }
      fetchMore={fetchMore}
      footer={disabled ? <LocalisedSttingInfo /> : undefined}
      header={{
        title,
        description,
      }}
      items={certificateTemplates}
      onClose={onClose}>
      {certificateTemplates?.length ? (
        <ContentListModal.Body
          className="flex-1"
          itemRenderer={(index: number) => {
            const currentCertificate = certificateTemplates?.[index];
            const isSelected = currentCertificate.id === selectedItem;

            if (!currentCertificate) {
              return null;
            }

            return (
              <CertificateCard
                certificate={currentCertificate}
                disabled={disabled}
                isSelected={isSelected}
                onSelect={onSelect}
              />
            );
          }}
        />
      ) : initialFetching ? (
        <Loader />
      ) : (
        <EmptyState
          Icon={Certificate2Icon}
          className="-mt-60 h-full w-full"
          description={<Trans i18nKey={'Certificate:NoCertificateDescription'} />}
          title={t('Certificate:NoCertificate')}
        />
      )}
    </ContentListModal.Main>
  );
}

export default React.memo(AttachCertificateDialog);
